import {
  type CreatePolicyInput,
  BackupPoliciesApi,
  CreateBackupPolicyApi,
  DeleteBackupPolicyApi,
  GetBackupPolicyApi,
  ListBackupPoliciesApi,
  type BackupPolicyConfig,
  type Condition,
  type Configuration,
  type PreviewBackupPolicyResourcesRequest,
  PreviewBackupPolicyResourcesApi,
  UpdateBackupPolicyApi,
} from '@repo/api-gw-sdk';
import type { ColumnSort, PaginationState } from '@tanstack/react-table';
import type { SWRConfiguration } from 'swr';

import { toJsonText } from '@/components/queryBuilder/QueryLanguageConverter';

import type { HttpClient } from './httpClient';
import { normalizeInventoryResources } from './inventory';

const basesUrl = '/backup-policies';
const v1BasesUrl = '/backup-policies-v1';

export const backupPolicy = (
  httpClient: HttpClient,
  config: Configuration,
  currentProjectId: string
) => {
  const listBackupPoliciesApi = new ListBackupPoliciesApi(config);
  const getBackupPolicyApi = new GetBackupPolicyApi(config);
  const backupPoliciesApi = new BackupPoliciesApi(config);
  const createBackupPolicyApi = new CreateBackupPolicyApi(config);
  const updateBackupPolicyApi = new UpdateBackupPolicyApi(config);
  const deleteBackupPolicyApi = new DeleteBackupPolicyApi(config);
  const previewBackupPolicyResourcesApi = new PreviewBackupPolicyResourcesApi(
    config
  );
  return {
    // Server-side resources preview for a draft policy: the backup engine
    // computes the matching condition, so the list agrees with what the policy
    // would actually attach to.
    usePreviewResources: (
      draft: PreviewBackupPolicyResourcesRequest,
      pagination: PaginationState,
      sorting: ColumnSort
    ) =>
      httpClient.executeWithConfig(
        (
          projectId: string,
          pageIndex: number,
          pageSize: number,
          sort: string,
          body: PreviewBackupPolicyResourcesRequest
        ) =>
          previewBackupPolicyResourcesApi
            .previewBackupPolicyResources(
              projectId,
              pageIndex,
              pageSize,
              sort,
              body
            )
            .then((response) => {
              normalizeInventoryResources(response.resources);
              return response;
            }),
        basesUrl,
        'preview-resources',
        {},
        currentProjectId,
        pagination.pageIndex,
        pagination.pageSize,
        `${sorting.id}:${sorting.desc ? 'desc' : 'asc'}`,
        draft
      ),
    useList: (
      pageIndex?: number,
      pageSize?: number,
      filter?: Condition,
      options?: SWRConfiguration,
      skip?: boolean
    ) =>
      httpClient.executeWithConfig(
        listBackupPoliciesApi.listPolicies.bind(listBackupPoliciesApi),
        skip ? null : basesUrl,
        '',
        options,
        currentProjectId,
        pageIndex,
        pageSize ? pageSize : 2000,
        filter ? toJsonText(filter) : undefined
      ),
    useGoogleWorkspaces: () =>
      httpClient.execute(
        backupPoliciesApi.listGoogleWorkspacesWithPolicyStatus.bind(
          backupPoliciesApi
        ),
        v1BasesUrl,
        'google-workspaces',
        currentProjectId
      ),
    useGet: (policyId: string | null) =>
      httpClient.execute(
        getBackupPolicyApi.getPolicy.bind(getBackupPolicyApi),
        policyId ? basesUrl : null,
        policyId ?? '',
        currentProjectId,
        policyId ?? ''
      ),
    useGetV1: (policyId: string | null) =>
      httpClient.execute(
        backupPoliciesApi.getBackupPolicy.bind(backupPoliciesApi),
        policyId ? v1BasesUrl : null,
        policyId ?? '',
        currentProjectId,
        policyId ?? ''
      ),
    create: (policy: CreatePolicyInput) =>
      createBackupPolicyApi.createPolicy(currentProjectId, policy),
    update: (policy: BackupPolicyConfig) =>
      updateBackupPolicyApi.updatePolicy(currentProjectId, policy.id, policy),
    delete: (policyId: string) =>
      deleteBackupPolicyApi.deletePolicy(currentProjectId, policyId),
  };
};
